/**
 * AdminPendingUGCPage - Pass 2
 * /admin/pending-ugc
 *
 * Lists every pending master-service / master-product / master-brand
 * submitted by hosts. Admin can approve (map to existing or create new)
 * or reject with notes.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const TABS = [
  { id: "services", label: "Services" },
  { id: "products", label: "Products" },
  { id: "brands", label: "Brands" },
];

export default function AdminPendingUGCPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ services: [], products: [], brands: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("services");
  const [actionItem, setActionItem] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [notes, setNotes] = useState("");
  const [mappedId, setMappedId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/pending-ugc`, { headers: auth() });
      if (!r.ok) throw new Error("Failed to load pending queue");
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitAction = async () => {
    if (!actionItem) return;
    setSubmitting(true);
    try {
      const url = `${API}/admin/pending-ugc/${active}/${actionItem.item_id}/${actionType}`;
      const r = await fetch(url, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          admin_notes: notes || null,
          mapped_master_id: actionType === "approve" && mappedId ? mappedId : null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const result = await r.json();
      toast.success(`Item ${actionType}d - ${result.action || "ok"}`);
      setActionItem(null);
      setActionType(null);
      setNotes("");
      setMappedId("");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const items = data[active] || [];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-pending-ugc-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/hub")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin Hub
            </Button>
            <h1 className="text-2xl font-light tracking-tight">Pending UGC moderation</h1>
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 border border-amber-200">
              {data.total || 0} pending
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        <p className="text-sm text-stone-600 mb-6">
          Hosts can submit new services, products, and brands. Until you act, their runtime keeps using the
          custom record while you moderate. Approve to promote into master data; reject with notes if it's
          a duplicate or invalid.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-stone-200">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                active === t.id
                  ? "border-black text-black font-medium"
                  : "border-transparent text-stone-500 hover:text-black"
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.label} <span className="ml-1 text-xs opacity-60">({(data.counts || {})[t.id] || 0})</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-stone-500" data-testid="empty-state">
            No pending {active}. All caught up.
          </div>
        ) : (
          <div className="space-y-3" data-testid="pending-items-list">
            {items.map((it) => (
              <div
                key={it.item_id}
                className="bg-white border border-stone-200 rounded-lg p-4 flex items-center justify-between gap-4"
                data-testid={`pending-item-${it.item_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{it.name || it.title || it.item_id}</div>
                  <div className="text-xs text-stone-500 mt-1 truncate">
                    {it.brand && `Brand: ${it.brand} · `}
                    {it.category && `Category: ${it.category} · `}
                    {it.product_group && `Group: ${it.product_group} · `}
                    Submitted by: {it.submitted_by || "host"}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setActionItem(it); setActionType("approve"); }}
                    data-testid={`approve-${it.item_id}`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-600 hover:bg-rose-50"
                    onClick={() => { setActionItem(it); setActionType("reject"); }}
                    data-testid={`reject-${it.item_id}`}
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action modal */}
        {actionItem && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="action-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-medium mb-2">
                {actionType === "approve" ? "Approve" : "Reject"} - {actionItem.name || actionItem.item_id}
              </h2>
              <p className="text-xs text-stone-500 mb-4">
                {actionType === "approve"
                  ? "Map to an existing master ID (paste below) or leave blank to create a new master record."
                  : "Reason for rejection - shown to the host in their email notification."}
              </p>
              {actionType === "approve" && (
                <Input
                  placeholder="Existing master_id (optional)"
                  value={mappedId}
                  onChange={(e) => setMappedId(e.target.value)}
                  data-testid="mapped-master-id-input"
                  className="mb-3"
                />
              )}
              <Textarea
                placeholder="Admin notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="admin-notes-input"
                className="mb-4"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setActionItem(null)} data-testid="cancel-action">
                  Cancel
                </Button>
                <Button
                  onClick={submitAction}
                  disabled={submitting}
                  className={actionType === "reject" ? "bg-rose-600 hover:bg-rose-700" : ""}
                  data-testid="confirm-action"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Confirm {actionType}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
